import { useState, useEffect } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import ProductCard from "@/components/ProductCard";
import Features from "@/components/Features";
import OrderFormModal from "@/components/OrderFormModal";
import PreOrderFormModal from "@/components/PreOrderFormModal";
import OnboardingTour from "@/components/OnboardingTour";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  available: boolean;
  available_for_preorder?: boolean;
}

const Index = () => {
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [preOrderModalOpen, setPreOrderModalOpen] = useState(false);
  const [availableItems, setAvailableItems] = useState<MenuItem[]>([]);
  const [preOrderItems, setPreOrderItems] = useState<MenuItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { toast } = useToast();

  // Check if user has seen onboarding tour
  useEffect(() => {
    const hasCompletedOnboarding = localStorage.getItem("onboarding_completed");
    if (!hasCompletedOnboarding) {
      // Delay showing tour slightly to let page load
      const timer = setTimeout(() => setShowOnboarding(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    fetchMenuItems();
  }, []);

  const fetchMenuItems = async () => {
    // Fetch items available for immediate order
    const { data: available, error: availableError } = await supabase
      .from("menu_items")
      .select("*")
      .eq("available", true)
      .order("created_at", { ascending: false });

    if (availableError) {
      console.error("Error fetching available items:", availableError);
      toast({
        title: "Error",
        description: "Failed to load menu items",
        variant: "destructive",
      });
    } else {
      setAvailableItems(available || []);
    }

    // Fetch items available for pre-order only (not available for immediate order)
    const { data: preOrder, error: preOrderError } = await supabase
      .from("menu_items")
      .select("*")
      .eq("available_for_preorder", true)
      .eq("available", false)
      .order("created_at", { ascending: false });

    if (preOrderError) {
      console.error("Error fetching pre-order items:", preOrderError);
    } else {
      setPreOrderItems(preOrder || []);
    }
  };

  const handleOrderClick = (item?: MenuItem) => {
    if (item) {
      setSelectedItem(item);
    } else if (availableItems.length > 0) {
      setSelectedItem(availableItems[0]);
    }
    setOrderModalOpen(true);
  };

  const handlePreOrderClick = (item?: MenuItem) => {
    if (item) {
      setSelectedItem(item);
    } else if (preOrderItems.length > 0) {
      setSelectedItem(preOrderItems[0]);
    }
    setPreOrderModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      {showOnboarding && (
        <OnboardingTour onComplete={() => setShowOnboarding(false)} />
      )}
      <Header onOrderClick={() => handleOrderClick()} />
      <Hero onOrderClick={() => handleOrderClick()} />
      
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground mb-4">Our Menu</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We focus on one thing and do it perfectly - the most delicious Hyderabadi Bawarchi chicken biryani in Makati
            </p>
          </div>
          
          <Tabs defaultValue="order-now" className="max-w-2xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="order-now">Order Now</TabsTrigger>
              <TabsTrigger value="pre-order">Pre-Order</TabsTrigger>
            </TabsList>
            
            <TabsContent value="order-now" className="space-y-6">
              {availableItems.length > 0 ? (
                availableItems.map((item) => (
                  <ProductCard 
                    key={item.id} 
                    menuItem={item}
                    mode="order-now"
                    onOrderClick={() => handleOrderClick(item)}
                    onPreOrderClick={() => handlePreOrderClick(item)}
                  />
                ))
              ) : (
                <p className="text-center text-muted-foreground">No items available for immediate order</p>
              )}
            </TabsContent>
            
            <TabsContent value="pre-order" className="space-y-6">
              {preOrderItems.length > 0 ? (
                preOrderItems.map((item) => (
                  <ProductCard 
                    key={item.id} 
                    menuItem={item}
                    mode="pre-order"
                    onOrderClick={() => handlePreOrderClick(item)}
                    onPreOrderClick={() => handlePreOrderClick(item)}
                  />
                ))
              ) : (
                <p className="text-center text-muted-foreground">No items available for pre-order</p>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Features />
      
      <OrderFormModal 
        open={orderModalOpen} 
        onOpenChange={setOrderModalOpen}
        menuItem={selectedItem ? {
          id: selectedItem.id,
          name: selectedItem.name,
          price: selectedItem.price
        } : undefined}
      />
      
      <PreOrderFormModal 
        open={preOrderModalOpen} 
        onOpenChange={setPreOrderModalOpen}
        menuItem={selectedItem ? {
          id: selectedItem.id,
          name: selectedItem.name,
          price: selectedItem.price
        } : undefined}
      />
    </div>
  );
};

export default Index;
